// DEPENDENCIES
import { FormikProps, FormikValues } from "formik";

// TYPES
import {
  Step,
  Option,
  PersonType,
  EmploymentStatus,
  PitbullProvinces,
  StepTwoPF,
} from "../../types";

// UTILS
import {
  employmentStatusOptions,
  employmentYearsOptions,
  isAgrofinanzasOnboarding,
  isAllariaFondosOnboarding,
  isAllariaMasOnboarding,
  isAllariaOnboarding,
  isMobileViewport,
  yesOrNoBoolOptions,
} from "../../utils";
import {
  mediumSelectedAddressRequired,
  cbuNotRequired,
  cbuRequired,
  multipleSelectRequired,
  noSpecialCharactersStringRequired,
  radioOrSingleSelectRequired,
  smallString,
  smallStringRequired,
  zipCodeRequired,
} from "../../validations";
import {
  completeAddress,
  getDraftStepsToSend,
  getForeignTaxDetails,
  handleSendStep,
  resetFields,
} from "../../app/utils";
import { logAnalyticsAndHotjarEvent } from "../../main";

const employmentProfileStep = (
  activitiesOptions: Option[],
  countriesOptions: Option[],
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
  provinces: PitbullProvinces,
  onSendStep?: (
    formValues: FormikValues | null,
    step: Record<string, StepTwoPF>,
  ) => Promise<void>,
  hasCoownersStep?: boolean,
): Omit<Step, "step"> => {
  const foreignTaxCountryOptions = countriesOptions.filter(
    (country) => country.value !== "Argentina",
  );
  const provinceOptions = provinces.map((prov) => ({
    value: prov.name,
    label: prov.name,
  }));
  return {
    label: "employmentProfile",
    name: "Situación laboral",
    fields: [
      {
        name: "employmentProfile.employmentStatus",
        type: "select",
        label: "Situación laboral actual",
        placeholder: "Seleccioná una opción",
        validation: radioOrSingleSelectRequired(employmentStatusOptions),
        onChange: (_values, setFieldValue, _, setFieldTouched, element) =>
          changeEmploymentStatus(setFieldValue, setFieldTouched, element),
        options: employmentStatusOptions,
        selectType: "single",
        initialValue: "",
      },
      {
        name: "employmentProfile.years",
        type: "select",
        label: "Años de actividad",
        placeholder: "Seleccioná una opción",
        validation: radioOrSingleSelectRequired(employmentYearsOptions),
        conditional: {
          name: "employmentProfile.employmentStatus",
          value: ["DEPENDENCY_RELATIONSHIP", "MONOTAX", "AUTONOMOUS"],
        },
        options: employmentYearsOptions,
        selectType: "single",
        initialValue: "",
        dontScrollTo: true,
      },
      {
        name: "employmentProfile.activities",
        type: "combobox",
        label: "Rubro o actividad",
        placeholder: "Seleccioná las actividades que correspondan",
        validation: multipleSelectRequired(activitiesOptions),
        dynamicOptions: (formik: FormikProps<FormikValues>) =>
          getEmploymentActivitiesOptions(activitiesOptions, formik),
        selectType: "multiple-checkbox",
        confirmSelection: isMobileViewport(),
        initialValue: "",
      },
            {
        name: "bankAccountsBool",
        type: "radio",
        label: "¿Tenés una cuenta bancaria en Argentina?",
        validation: radioOrSingleSelectRequired(yesOrNoBoolOptions),
        options: yesOrNoBoolOptions,
        initialValue: "",
        preconfirmationLabel: "¿Cuenta bancaria argentina?",
      },
      {
        conditional: {
          name: "bankAccountsBool",
          value: "SI",
        },
        name: "employmentProfile.bankAccounts",
        type: "text",
        label: "Cuenta bancaria en Argentina",
        validation:
          isAllariaOnboarding() ||
          isAgrofinanzasOnboarding() ||
          isAllariaFondosOnboarding()
            ? cbuNotRequired
            : cbuRequired,
        initialValue: "",
        preconfirmationLabel: "CBU/CVU",
        placeholder: "Ingresá tu CBU o CVU",
        showOptionalLabel: true,
        description: "El presente CBU informado será utilizado como instrucción permanente para pagos futuros a menos que se indique lo contrario. Luego de la apertura de cuenta, se podrán editar y agregar nuevos CBU/CVUs.",
        infoText:
          "La cuenta ingresada debe ser de tu misma titularidad.",
      },
      {
        name: "foreignTaxDetailsBool",
        type: "radio",
        label: "¿Tenés residencia fiscal fuera de Argentina?",
        validation: radioOrSingleSelectRequired(yesOrNoBoolOptions),
        options: yesOrNoBoolOptions,
        initialValue: "",
      },
      {
        conditional: {
          name: "foreignTaxDetailsBool",
          value: "SI",
        },
        name: fullForeignAddressFields.country,
        type: "select",
        label: "País de residencia fiscal",
        placeholder: "Seleccioná una opción",
        validation: radioOrSingleSelectRequired(foreignTaxCountryOptions),
        options: foreignTaxCountryOptions,
        selectType: "single",
        initialValue: "",
        onChange: (_, setFieldValue) =>
          resetFields(foreignAddressFieldsNames, setFieldValue),
        preconfirmationLabel: "Residencia fiscal",
      },
      {
        conditional: {
          name: "foreignTaxDetailsBool",
          value: "SI",
        },
        name: foreignAddressFields.fullAddress,
        type: "address",
        label: "Domicilio",
        validation: mediumSelectedAddressRequired,
        initialValue: "",
        autoSelect: foreignAddressFields.fullAddress,
        country: fullForeignAddressFields.country,
        onChange: (values, setFieldValue) => {
          const {
            formattedAddress: {
              postalCode,
              state,
              city,
              municipality,
              street,
              streetNumber,
              country,
            },
          } = values;

          const address = {
            country,
            state,
            city,
            municipality,
            street,
            number: streetNumber,
            zipCode: postalCode,
          };

          return completeAddress(
            foreignAddressFields,
            address,
            setFieldValue,
            getProvincesFilteredByCountry,
          );
        },
        autoComplete: "off",
        formatInPreConfirmation: (value) =>
          value ? value?.split(",")?.[0] : "",
      },
      {
        conditional: {
          name: "foreignTaxDetailsBool",
          value: "SI",
        },
        name: fullForeignAddressFields.floor,
        type: "text",
        label: "Piso",
        validation: smallString,
        initialValue: "",
        columns: 2,
      },
      {
        conditional: {
          name: "foreignTaxDetailsBool",
          value: "SI",
        },
        name: fullForeignAddressFields.apartment,
        type: "text",
        label: "Departamento",
        validation: smallString,
        initialValue: "",
        columns: 2,
      },
      {
        conditional: {
          name: "foreignTaxDetailsBool",
          value: "SI",
        },
        name: fullForeignAddressFields.zipCode,
        type: "text",
        label: "Código postal",
        validation: zipCodeRequired,
        initialValue: "",
        columns: 1,
      },
      {
        conditional: {
          name: "foreignTaxDetailsBool",
          value: "SI",
        },
        name: fullForeignAddressFields.municipality,
        type: "text",
        label: "Localidad",
        validation: smallStringRequired,
        initialValue: "",
        columns: 2,
      },
      {
        conditional: {
          name: "foreignTaxDetailsBool",
          value: "SI",
        },
        name: fullForeignAddressFields.state,
        type: "select",
        label: "Provincia",
        searchable: true,
        validation: radioOrSingleSelectRequired(provinceOptions),
        initialValue: "",
        dynamicOptions: (formik) => {
          const country =
            formik.values.employmentProfile?.foreignTaxDetails?.country || "";
          const provinces = getProvincesFilteredByCountry(country);
          const provincesOption = provinces.map((prov) => ({
            value: prov.name,
            label: prov.name,
          }));
          return provincesOption;
        },
        columns: 2,
      },
      {
        conditional: {
          name: "foreignTaxDetailsBool",
          value: "SI",
        },
        name: "employmentProfile.foreignTaxDetails.idNumber",
        type: "text",
        label: "Identificación fiscal",
        validation: noSpecialCharactersStringRequired,
        initialValue: "",
      },
    ],
    onContinue: async (values: FormikValues, dispatch, state) => {
      if (!state || !dispatch)
        throw new Error("Error: state or dispatch not found");

      const {
        onboardingData: { sendedSteps, formValues },
      } = state;

      const step = getParsedStep(values);

      if (onSendStep) {
        await onSendStep(formValues, { stepTwo: step });
      } else {
        await handleSendStep({
          dispatch,
          personType: "PHYSICAL" as PersonType,
          email: values.email,
          sendedSteps,
          stepId: 2,
          step,
        });

        // Send empty coowners step only when the flow has no coowners step of
        // its own. Otherwise that step sends the real coowners later.
        if (!hasCoownersStep) {
          await handleSendStep({
            dispatch,
            personType: "PHYSICAL" as PersonType,
            email: values.email,
            // Include the step just sent: the store isn't updated yet at this point
            sendedSteps: getDraftStepsToSend(sendedSteps, step),
            stepId: 3,
            step: {
              "@type": "3_PHYSICAL",
              coOwners: [],
            },
          });
        }
      }

      logAnalyticsAndHotjarEvent("step_employment_status_completed_pf");
    },
  };
};

const foreignAddressFields = {
  fullAddress: "employmentProfile.foreignTaxDetails.fullAddress",
  municipality: "employmentProfile.foreignTaxDetails.municipality",
  state: "employmentProfile.foreignTaxDetails.state",
  zipCode: "employmentProfile.foreignTaxDetails.zipCode",
  city: "employmentProfile.foreignTaxDetails.city",
  street: "employmentProfile.foreignTaxDetails.street",
  number: "employmentProfile.foreignTaxDetails.number",
  floor: "employmentProfile.foreignTaxDetails.floor",
  apartment: "employmentProfile.foreignTaxDetails.apartment",
};

const fullForeignAddressFields = {
  country: "employmentProfile.foreignTaxDetails.country",
  ...foreignAddressFields,
};

const foreignAddressFieldsNames = Object.values(foreignAddressFields);

const changeEmploymentStatus = (
  setFieldValue: FormikProps<FormikValues>["setFieldValue"],
  setFieldTouched?: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean,
  ) => void,
  element?: React.ChangeEvent<HTMLInputElement>,
) => {
  const activitiesField = "employmentProfile.activities";
  const yearsOfActivityField = "employmentProfile.years";

  setFieldValue(yearsOfActivityField, []);
  if (setFieldTouched) setFieldTouched(yearsOfActivityField, false);

  if (element)
    switch (element.toString()) {
      case "STUDENT":
        setFieldValue(activitiesField, ["Estudiante"]);
        break;
      case "RETIRED":
        setFieldValue(activitiesField, ["Jubilado/a y/o Pensionado/a"]);
        break;
      default:
        setFieldValue(activitiesField, []);
        break;
    }
};

const getEmploymentActivitiesOptions = (
  activitiesOptions: Option[],
  formik: FormikProps<FormikValues>,
): Option[] => {
  const employmentProfile =
    (formik.values.employmentProfile?.employmentStatus as EmploymentStatus) ||
    "";

  const activitiesByProfile: Partial<Record<EmploymentStatus, string[]>> = {
    DEPENDENCY_RELATIONSHIP: [
      "Empleado Público",
      "Empleado/Apoderado",
      "Docente",
      "Diplomático/Funcionario Público",
      "Policía/Militar/otras Fuerzas de Seguridad",
    ],
    STUDENT: ["Estudiante"],
    RETIRED: ["Jubilado/a y/o Pensionado/a"],
    UNEMPLOYED: ["Desocupado", "Ama de Casa"],
  };

  if (
    !employmentProfile ||
    !(employmentProfile in activitiesByProfile) ||
    !activitiesByProfile[employmentProfile]
  ) {
    return activitiesOptions;
  }

  return activitiesByProfile[employmentProfile].map((activity) => ({
    label: activity,
    value: activity,
  }));
};

const getParsedStep = (values: FormikValues) => {
  const {
    foreignTaxDetailsBool,
    bankAccountsBool,
    employmentProfile: {
      employmentStatus,
      years,
      activities,
      bankAccounts,
      foreignTaxDetails,
    },
  } = values;

  const getBankAccounts = () => {
    if (typeof bankAccounts !== "string") return [];
    if (bankAccountsBool === "SI" && isAllariaMasOnboarding()) {
      return [{ cxu: bankAccounts }];
    }

    return bankAccounts ? [{ cxu: bankAccounts }] : [];
  };

  return {
    "@type": "2_PHYSICAL",
    employmentProfile: {
      employmentStatus,
      ...(years && { years }),
      activities,
      bankAccounts: [...getBankAccounts()],
      ...getForeignTaxDetails(foreignTaxDetailsBool, foreignTaxDetails),
    },
  };
};

export default employmentProfileStep;
